// Service layer for /api/secure/me/sessions/*. Mock ↔ real dispatch.
//
// Profile context flows automatically: real backend reads the
// patientId query param that patientApi.js attaches; the mock takes the
// active profile object directly.

import { shouldUseMocks } from './mockApi'
import * as mock from './mockApi/sessionsApi'
import { patientGet, patientPost } from './patientApi'

const BASE = '/api/secure/me/sessions'
const CTX = { requirePatientContext: true }

export async function listSessions(profile, status, page = 1, pageSize = 20) {
  if (shouldUseMocks()) return mock.listSessions(profile, status, page, pageSize)
  const res = await patientGet(BASE, { ...CTX, params: { status, page, pageSize } })
  return res?.data || { items: [], page, pageSize, totalItems: 0, hasMore: false }
}

export async function getSession(profile, eventId) {
  if (shouldUseMocks()) return mock.getSession(profile, eventId)
  const res = await patientGet(`${BASE}/${encodeURIComponent(eventId)}`, CTX)
  return res?.data || null
}

export async function reschedule(profile, eventId, body) {
  if (shouldUseMocks()) return mock.reschedule(profile, eventId, body)
  const res = await patientPost(`${BASE}/${encodeURIComponent(eventId)}/reschedule`, body, CTX)
  return res?.data
}

export async function cancel(profile, eventId, body) {
  if (shouldUseMocks()) return mock.cancel(profile, eventId, body)
  await patientPost(`${BASE}/${encodeURIComponent(eventId)}/cancel`, body || {}, CTX)
  return null
}

export async function submitFeedback(profile, eventId, body) {
  if (shouldUseMocks()) return mock.submitFeedback(profile, eventId, body)
  const res = await patientPost(`${BASE}/${encodeURIComponent(eventId)}/feedback`, body, CTX)
  return res?.data
}

export async function getFeedback(profile, eventId) {
  if (shouldUseMocks()) return mock.getFeedback(profile, eventId)
  const res = await patientGet(`${BASE}/${encodeURIComponent(eventId)}/feedback`, CTX)
  return res?.data
}

export async function requestSession(profile, body) {
  if (shouldUseMocks()) return mock.requestSession(profile, body)
  const res = await patientPost(`${BASE}/request`, body, CTX)
  return res?.data
}

export async function listRequests(profile) {
  if (shouldUseMocks()) return mock.listRequests(profile)
  const res = await patientGet(`${BASE}/requests`, CTX)
  return res?.data || []
}

/**
 * Fetch the receipt PDF for a past session as a Blob, return the blob +
 * filename + previewable object URL so callers can BOTH save the file to
 * disk AND show it inline (e.g. in a drawer). The browser save itself is
 * triggered iff `download !== false` so a caller that just wants to preview
 * can opt out of the disk write.
 *
 * Backend endpoint: GET /api/secure/me/sessions/{eventId}/receipt.pdf?patientId=…&lang=…
 *
 * Mocks have no PDF generator behind them — falls through to a synthetic
 * text blob masquerading as a receipt so the UI flow is exercisable in
 * mock mode without a backend roundtrip.
 *
 * @param {object} profile  active PatientProfile
 * @param {string} eventId  booked-event id
 * @param {object} [opts]   { lang?: 'en'|'hi'|…, download?: boolean (default true) }
 * @returns {Promise<{filename: string, blob: Blob, blobUrl: string, mimeType: string}>}
 *   The caller is responsible for revoking blobUrl via URL.revokeObjectURL
 *   when the preview drawer closes.
 */
export async function downloadReceiptPdf(profile, eventId, opts = {}) {
  const filenameSuggested = `aumhum-receipt-${eventId}.pdf`
  const shouldDownload = opts.download !== false
  if (shouldUseMocks()) {
    const note =
      `AUMHUM mock receipt — ${eventId}\n` +
      `Profile: ${profile?.displayName || profile?.id}\n` +
      `Generated: ${new Date().toISOString()}\n\n` +
      'Real receipts come from /api/secure/me/sessions/{eventId}/receipt.pdf.'
    const filename = filenameSuggested.replace('.pdf', '.txt')
    const blob = new Blob([note], { type: 'text/plain' })
    const blobUrl = URL.createObjectURL(blob)
    if (shouldDownload) triggerBlobDownload(blob, filename)
    return { filename, blob, blobUrl, mimeType: 'text/plain' }
  }
  const res = await patientGet(
    `${BASE}/${encodeURIComponent(eventId)}/receipt.pdf`,
    {
      ...CTX,
      profileId: profile?.id,
      responseType: 'blob',
      params: opts.lang ? { lang: opts.lang } : undefined,
    },
  )
  const cd = res?.headers?.['content-disposition'] || res?.headers?.['Content-Disposition']
  const filename = filenameFromContentDisposition(cd) || filenameSuggested
  const mimeType =
    res?.headers?.['content-type'] || res?.headers?.['Content-Type'] || 'application/pdf'
  const blob = res?.data instanceof Blob
    ? res.data
    : new Blob([res?.data || ''], { type: mimeType })
  const blobUrl = URL.createObjectURL(blob)
  if (shouldDownload) triggerBlobDownload(blob, filename)
  return { filename, blob, blobUrl, mimeType }
}

function filenameFromContentDisposition(cd) {
  if (!cd) return null
  // Permissive parser — handles `attachment; filename="name.pdf"` and the
  // RFC 5987 `filename*=UTF-8''name.pdf` variant. Servers in this project
  // emit the simple form; the * branch is defensive.
  const star = /filename\*=UTF-8''([^;]+)/i.exec(cd)
  if (star) {
    try {
      return decodeURIComponent(star[1].trim())
    } catch (e) {
      /* fall through */
    }
  }
  const plain = /filename="?([^";]+)"?/i.exec(cd)
  return plain ? plain[1].trim() : null
}

function triggerBlobDownload(blob, filename) {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  // Some browsers require the link be in the DOM for the click to register.
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  // Defer revoke so Safari has time to start the download from the URL.
  setTimeout(() => URL.revokeObjectURL(url), 5_000)
}
